// File: src/client/services/aiService.ts

import { type AnalyticsEvent, track } from "@/client/analytics/mixpanel";
import { axios } from "@/client/libs/axios";

// track() is already failure-tolerant; this just keeps the call sites terse.
const trackMixpanel = (event: AnalyticsEvent, props?: Record<string, any>) => {
  track(event, props);
};
export const aiService = {
  processStep: async (step: string, input: string, previousData: any) => {
    try {
      const response = await axios.post("/ai/process-step", { step, input, previousData });
      return response.data;
    } catch (error) {
      console.error("Error processing step:", error);
      throw error;
    }
  },

  analyzePersonality: async (responses: Record<string, string>) => {
    try {
      const response = await axios.post("/ai/analyze-personality", { responses });
      return response.data;
    } catch (error) {
      console.error("Error analyzing personality:", error);
      throw error;
    }
  },

  analyzeJobDescription: async (jobDescription: string) => {
    try {
      const response = await axios.post("/ai/analyze-job-description", { jobDescription });
      trackMixpanel("resume_ai_edited", { action: "analyzeJobDescription" });
      return response.data;
    } catch (error) {
      console.error("Error analyzing job description:", error);
      throw error;
    }
  },

  generateResume: async (resumeData: any) => {
    try {
      const response = await axios.post("/ai/generate-resume", { resumeData });
      return response.data;
    } catch (error) {
      console.error("Error generating resume:", error);
      throw error;
    }
  },

  suggestSkills: async (userSkills: string[], jobSkills: string[]) => {
    try {
      const response = await axios.post("/ai/suggest-skills", { userSkills, jobSkills });
      return response.data;
    } catch (error) {
      console.error("Error suggesting skills:", error);
      throw error;
    }
  },

  generateExperienceSuggestion: async (jobDescription: string, personalityAssessment: any) => {
    try {
      const response = await axios.post("/ai/generate-experience-suggestion", {
        jobDescription,
        personalityAssessment,
      });
      trackMixpanel("resume_ai_edited", { action: "generateExperienceSuggestion" });
      return response.data;
    } catch (error) {
      console.error("Error generating experience suggestion:", error);
      throw error;
    }
  },

  generateExperienceSuggestions: async (
    jobDescription: string,
    jobCompanyName: string,
    jobTitleAndResponsibilities: string,
  ) => {
    try {
      const response = await axios.post("/ai/generate-experience-suggestions", {
        jobDescription,
        jobCompanyName,
        jobTitleAndResponsibilities,
      });
      trackMixpanel("resume_ai_edited", { action: "generateExperienceSuggestions" });
      return response.data.suggestions;
    } catch (error) {
      console.error("Error generating experience suggestions:", error);
      throw error;
    }
  },

  generateFollowUpQuestions: async (
    jobDescription: string,
    experienceOverview: string,
    personalityAssessment: any,
  ) => {
    try {
      const response = await axios.post("/ai/generate-follow-up-questions", {
        jobDescription,
        experienceOverview,
        personalityAssessment,
      });

      console.log("Raw response:", response.data); // Log the raw response

      if (response.data && Array.isArray(response.data.followUpQuestions)) {
        return response.data.followUpQuestions;
      }

      throw new Error("Unexpected response format");
    } catch (error) {
      console.error("Error generating follow-up questions:", error);
      throw error;
    }
  },

  generateUserSummary: async (
    jobDescription: string,
    workExp: Array<{
      jobCompanyName: string;
      jobtitleAndResp?: string;
      jobTitleAndResponsibilities?: string;
      jobUniqueImpact: string;
    }>,
    personality: Array<{
      question: string;
      ans: string;
    }>,
    skills?: string[],
    currentSummary?: string,
  ) => {
    try {
      const normalizedWorkExp = workExp.map((exp) => ({
        jobCompanyName: exp.jobCompanyName,
        jobTitleAndResponsibilities: exp.jobTitleAndResponsibilities ?? exp.jobtitleAndResp ?? "",
        jobUniqueImpact: exp.jobUniqueImpact,
      }));

      const response = await axios.post("/ai/generate-user-summary", {
        jobDescription,
        workExp: normalizedWorkExp,
        personality,
        skills,
        currentSummary,
      });
      trackMixpanel("resume_ai_edited", { action: "generateUserSummary" });
      return response.data.summary;
    } catch (error) {
      console.error("Error generating user summary:", error);
      throw error;
    }
  },

  generateFinalResume: async (
    jobDescription: string,
    personalityAssessment: any,
    experienceOverview: string,
    followUpResponses: any[],
    selectedSkills: string[],
  ) => {
    try {
      const response = await axios.post("/ai/generate-final-resume", {
        jobDescription,
        personalityAssessment,
        experienceOverview,
        followUpResponses,
        selectedSkills,
      });
      console.log("Raw response in aiservice.ts:", response.data); // Log the raw response
      trackMixpanel("resume_ai_edited", { action: "generateFinalResume" });
      return response.data;
    } catch (error) {
      console.error("Error generating final resume:", error);
      throw error;
    }
  },

  // ===========================
  // Inline AI Field Assistance
  // ===========================

  generateHeadline: async (
    workExperiences?: Array<{
      company: string;
      position: string;
      summary: string;
    }>,
    jobDescription?: string,
    currentHeadline?: string,
  ) => {
    try {
      const response = await axios.post("/ai/generate-headline", {
        workExperiences,
        jobDescription,
        currentHeadline,
      });
      trackMixpanel("resume_ai_edited", { action: "generateHeadline" });
      return response.data;
    } catch (error) {
      console.error("Error generating headline:", error);
      throw error;
    }
  },

  generateExperienceFromTitle: async (
    company: string,
    position: string,
    dateRange?: string,
    jobDescription?: string,
    existingExperiences?: Array<{
      company: string;
      position: string;
      summary: string;
    }>,
  ) => {
    try {
      const response = await axios.post("/ai/generate-experience-from-title", {
        company,
        position,
        dateRange,
        jobDescription,
        existingExperiences,
      });
      return response.data;
    } catch (error) {
      console.error("Error generating experience from title:", error);
      throw error;
    }
  },

  suggestSkillCategory: async (
    keywords: string[],
    existingSkills?: Array<{
      name: string;
      keywords: string[];
    }>,
    jobDescription?: string,
  ) => {
    try {
      const response = await axios.post("/ai/suggest-skill-category", {
        keywords,
        existingSkills,
        jobDescription,
      });
      return response.data;
    } catch (error) {
      console.error("Error suggesting skill category:", error);
      throw error;
    }
  },

  generateSkillsFromExperience: async (
    experienceText: string,
    jobDescription?: string,
    existingSkills?: string[],
  ) => {
    try {
      const response = await axios.post("/ai/generate-skills-from-experience", {
        experienceText,
        jobDescription,
        existingSkills,
      });
      return response.data;
    } catch (error) {
      console.error("Error generating skills from experience:", error);
      throw error;
    }
  },

  // ===========================
  // Inline rewrite actions (unified AI Assist)
  // ===========================

  improveWriting: async (text: string, jobDescription?: string): Promise<string> => {
    try {
      const response = await axios.post("/ai/improve-writing", { text, jobDescription });
      trackMixpanel("resume_ai_edited", { action: "improveWriting" });
      return response.data.text ?? text;
    } catch (error) {
      console.error("Error improving writing:", error);
      throw error;
    }
  },

  fixGrammar: async (text: string): Promise<string> => {
    try {
      const response = await axios.post("/ai/fix-grammar", { text });
      trackMixpanel("resume_ai_edited", { action: "fixGrammar" });
      return response.data.text ?? text;
    } catch (error) {
      console.error("Error fixing grammar:", error);
      throw error;
    }
  },

  changeTone: async (text: string, mood: string, jobDescription?: string): Promise<string> => {
    try {
      const response = await axios.post("/ai/change-tone", { text, mood, jobDescription });
      trackMixpanel("resume_ai_edited", { action: "changeTone" });
      return response.data.text ?? text;
    } catch (error) {
      console.error("Error changing tone:", error);
      throw error;
    }
  },
};
